<template>
  <div
    v-if="hasElementsInConfig"
    class="table"
  >
    <div class="table__content">
      <div class="table__content-wrapper" :style="percentageWidth ? 'width: 100%' : ''">
        <TableHeader
          :config="config"
          :order-by="orderBy"
          :sort-direction="sortDirection"
          @update-sort="updateSort"
          @update-filters="updateFilters"
          @change-order="changeColumnOrder"
        />
        <TableBody
          :config="config"
          :table-data="tableData"
          :clickable-rows="clickableRows"
          @row-click="(val) => $emit('row-click', val)"
          @btn-click="(val) => $emit('btn-click', val)"
          @delete-row="(val) => $emit('delete-row', val)"
        />
      </div>
    </div>
    <TableFooter
      :config="config"
      :current-page="currentPage"
      :pages="pages"
      @change-page="changePageHandler"
      @update-column="updateColumnHandler"
      @reset-settings="resetSettingsHandler"
    />
  </div>
</template>

<script>
/**
 * Таблица
 * @module Shared/Component/Table
 * @requires Shared/Table/TableHeader
 * @requires Shared/Table/TableBody
 * @requires Shared/Table/TableFooter
 * @vue-prop {String} tableId - id таблицы
 * (используется для сохранения настроек колонок пользователя)
 * @vue-prop {Array} tableConfig - конфигурация таблицы
 * @vue-prop {Array} tableData - данные для отображения
 * @vue-prop {Number} currentPage - номер текущей страницы
 * @vue-prop {Number} pages - общее количество страниц
 * @vue-prop {String} groupBy - поле, по которому сгруппированы данные в таблице
 * @vue-prop {Boolean} percentageWidth - переключает способ задания ширины колонок
 * (отностительная в процентах или фиксированная со скроллом)
 * @vue-data {Array} config - локальная копия настроек (подтягиваются настройки пользователя)
 * @vue-data {String} orderBy - имя поля, по которому выполняется сортировка
 * @vue-data {String} sortDirection - направление сортировки
 * @vue-data {Object} filters - объект с фильтрами { columnName: value }
 * @vue-computed {Boolean} hasElementsInConfig - Есть ли элементы в конфиге
 * */

/**
 * TableConfig
 * [
 *    title {String} - Заголовок столбца в колонке (также используется в настройках)
 *    filed {String} - имя поля в объекте данных
 *    sortable {Boolean} - можно ли сортировать эту колонку
 *    filterable {Boolean} - можно ли фильтровать эту колонку
 *    hidden {Boolean} - скрыта ли колонка (можно скрывать\показывать в настройках)
 *    columnWidth {String} - стили, которые применятся к столбцам в хедере и в таблице
 *      (можно задавать в пикселях, либо в процентах, если передать таблице флаг percentageWidth)
 *    emptyHeader {Boolean} - с этим флагом у столбца не будет подписи в хедере
 *    type {String} - тип колонки (delete - иконка удаления записи, эмитит событие 'delete')
 * ]
 */

import TableHeader from 'Shared/Table/TableHeader'
import TableBody from 'Shared/Table/TableBody'
import TableFooter from 'Shared/Table/TableFooter'

export default {
  name: 'Table',
  components: {
    TableHeader,
    TableBody,
    TableFooter
  },
  props: {
    tableId: {
      type: String,
      default: ''
    },
    tableConfig: {
      type: Array,
      default: () => [],
      validator(val) {
        if (val.length && !val.every(el => el.name)) {
          console.error('field name required for all config objects')
          return false
        }

        return true
      }
    },
    tableData: {
      type: Array,
      default: () => []
    },
    currentPage: {
      type: Number,
      default: 1
    },
    pages: {
      type: Number,
      default: 0
    },
    groupBy: {
      type: String,
      default: ''
    },
    percentageWidth: {
      type: Boolean,
      default: false
    },
    clickableRows: {
      type: Boolean,
      default() {
        return !!this.$listeners['row-click']
      }
    }
  },
  data() {
    return {
      config: this.prepareConfig(this.tableConfig),
      orderBy: '',
      sortDirection: '',
      filters: {}
    }
  },
  computed: {
    hasElementsInConfig() {
      return this.tableConfig.length > 0
    }
  },
  watch: {
    groupBy() {
      this.config = this.prepareConfig(this.config)
    }
  },
  created() {
    if (this.tableId) {
      this.getSettings()
    }
  },
  methods: {
    /**
     * Обработчик переключения сортировки
     * @param {String} columnName - имя колонки, по которой выполняется сортировка
     */
    updateSort(columnName) {
      if (columnName === this.orderBy) {
        this.toggleSortDirection()
      } else {
        this.orderBy = columnName
        this.sortDirection = 'asc'
      }
      this.$emit('update-sort', {
        orderBy: this.orderBy,
        direction: this.sortDirection
      })
      this.makeRequest()
    },
    /**
     * Обработчик обновления фильтров
     * @param {String} columnName - имя колонки, по которой выполняется сортировка
     * @param {String} value - значение, по которому нужно отфильтровать
     */
    updateFilters(columnName, value) {
      this.filters[columnName] = value
      this.$emit('update-filters', columnName, value)
      this.makeRequest()
    },
    /**
     * Переключатель направления сортировки
     */
    toggleSortDirection() {
      switch (this.sortDirection) {
        case 'asc':
          this.sortDirection = 'desc'
          break
        case 'desc':
          this.orderBy = ''
          this.sortDirection = ''
          break
        default:
          this.sortDirection = 'asc'
          break
      }
    },
    /**
     * Подготовка конфига (из настроек или из пропсов)
     * @param {Array} config - конфиг таблицы
     * @returns {Array} подготовленный конфиг
     */
    prepareConfig(config) {
      const result = []
      if (config) {
        config.forEach(configElement => {
          result.push({
            ...configElement,
            extandable: configElement.field === this.groupBy,
            hidden: configElement.field === this.groupBy ? false : configElement.hidden || false
          })
        })
      }
      return result
    },
    /**
     * Обработчик обновления настроек колонки пользователем
     * @param {Object} params
     * @param {String} params.value - значение на которое нужно обновить
     * @param {String} params.paramName - имя параметра
     * @param {String} params.columnName - имя колонки
     */
    updateColumnHandler(params) {
      const configElement = this.config.find(element => element.field === params.columnName)
      configElement[params.paramName] = params.value
      this.saveSettings()
    },
    /**
     * Создание события для обновления данных (используется при обновлении фильтров, сортировки)
     */
    makeRequest() {
      this.$emit('request', {
        orderBy: this.orderBy,
        sortDirection: this.sortDirection,
        filters: this.filters,
        page: this.page
      })
    },
    /**
     * Обработчик переключения страницы
     * @param {Number} newPage - номер новой страницы
     */
    changePageHandler(newPage) {
      this.page = newPage
      this.makeRequest()
    },
    /**
     * Обработчик изменения порядка колонок
     * @param {Object} params
     * @param {Number} params.from - имя перемещаемой колонки
     * @param {Number} params.to - имя целевой колонки
     */
    changeColumnOrder(params) {
      const fromIndex = this.config.findIndex(element => element.field === params.from)
      const toIndex = this.config.findIndex(element => element.field === params.to)
      this.config.splice(toIndex, 0, this.config.splice(fromIndex, 1)[0])
      this.saveSettings()
    },
    /**
     * Получить настройки из localStorage
     */
    getSettings() {
      if (this.tableId) {
        const configFromLS = JSON.parse(localStorage.getItem(`customTable${this.tableId}`))
        if (configFromLS) {
          this.config = this.prepareConfig(
            configFromLS.map(
              configEl => ({
                ...this.tableConfig.find(
                  tableConfigEl => tableConfigEl.name === configEl.name
                ),
                ...configEl
              })
            )
          )
        }
      }
    },
    /**
     * Сохранить настройки в localStorage
     */
    saveSettings() {
      if (this.tableId) {
        localStorage.setItem(`customTable${this.tableId}`, JSON.stringify(this.config))
      }
    },
    /**
     * Сбросить сохранённые настройки
     */
    resetSettingsHandler() {
      this.config = this.prepareConfig(this.tableConfig)
      localStorage.removeItem(`customTable${this.tableId}`)
    }
  }
}
</script>

<style lang="scss" scoped>
.table {
  &__content {
    overflow-x: auto;
    overflow-y: hidden;
    border-radius: 10px 10px 0 0;
    box-shadow: 0px 0px 0px 1px $table-border-color;
  }

  &__content-wrapper {
    width: fit-content;
  }
}
</style>
